/**
 * Money formatting helpers for surfaces that work in the canonical
 * USD store currency (admin/back-office).
 *
 * USD-BASE PRICING (cutover 2026-06-19): store currency switched from
 * AUD-inc-GST to USD with no GST. Function names retained for back-compat
 * — `formatAudCents` now formats USD. Consumer-facing surfaces still MUST
 * use `useCallerCountry().formatLocalAmount()` which respects the user's
 * selected display currency via DISPLAY_CURRENCIES.
 *
 * Core memory: "Monetary values: enforce formatLocalAmount() / formatRate().
 * No hardcoded currency symbols or math."
 */

const audFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const audFormatterRounded = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Format integer cents as a localized USD currency string (admin/back-office). */
export function formatAudCents(
  cents: number | null | undefined,
  options: { rounded?: boolean; placeholder?: string } = {},
): string {

  const { rounded = false, placeholder = "-" } = options;
  if (cents === null || cents === undefined || Number.isNaN(cents)) {
    return placeholder;
  }
  const dollars = cents / 100;
  return rounded ? audFormatterRounded.format(dollars) : audFormatter.format(dollars);
}

/** Same as formatAudCents but accepts dollar values (legacy callers). */
export function formatAudDollars(
  dollars: number | null | undefined,
  options: { rounded?: boolean; placeholder?: string } = {},
): string {
  if (dollars === null || dollars === undefined || Number.isNaN(dollars)) {
    return options.placeholder ?? "-";
  }
  return options.rounded
    ? audFormatterRounded.format(dollars)
    : audFormatter.format(dollars);
}
